import React, { useState, useEffect, useRef } from 'react';
import { IoMdArrowBack } from 'react-icons/io';
import { FiChevronDown } from 'react-icons/fi';
import { fmt } from '@/helpers/quoteFunctions';
import { centsToDollors, formattedDate, splitEventDateAndTime, toCaptilize } from '@/helpers/TimeFormat';
import { useAppSelector } from '@/hooks/hook';
import { GET } from '@/services/api';
import { API_ROUTES } from '@/services/environment';
import { convertCentsToDollars, Loader } from '@thdinc/hpf-shared';
import SearchSelect from '@/components/StyledSelect/SearchSelect';
import { CocktailProviders, EVENT_TYPE_MAP, TypeOfServiceOptions } from '@/pages/Quote/constants/Options';
import { Drinker } from '@/pages/Quote/components/Drinker';
const { createQuote } = API_ROUTES;

interface PreviewQuoteTemplateProps {
    viewDetailModalOpen: boolean;
    setViewDetailModalOpen: (open: boolean) => void;
    quote?: any;
    isTemplateOpen?: boolean;
    updateQuoteStage?: (id: string, stage: string) => void;
}

const PreviewQuoteTemplate: React.FC<PreviewQuoteTemplateProps> = ({
    viewDetailModalOpen,
    setViewDetailModalOpen,
    quote,
    isTemplateOpen,
    updateQuoteStage
}) => {

    const [selected, setSelected] = useState('Draft');
    const [open, setOpen] = useState(false);
    const [quoteData, setQuote] = useState<any>();
    const [quoteLoading, setQuoteLoading] = useState<boolean>(false);
    const options = ['Draft', 'Pending', 'Approved', 'Rejected'];
    const { refreshToken } = useAppSelector((store) => store.user);
    const [selectedStages, setSelectedStages] = useState<Record<string, string>>({});
    const isTemplate = !!(quoteData?.is_template ?? quote?.is_template);
    const bartenderPackageCompany = quoteData?.quote_bartender_package?.
        filter((item: any) => item?.provider == 'companyProvide');
    const bartenderPackageCustomer = quoteData?.quote_bartender_package?.
        filter((item: any) => item?.provider != 'companyProvide');
    const cocktailCompany = quoteData?.quote_cocktails?.
        filter((item: any) => item?.provider == 'store-provide' || item?.provider == 'business-provide');
    const cocktailCustomer = quoteData?.quote_cocktails?.
        filter((item: any) => item?.provider == 'customer-provide');
    const cocktailHoProvide = quoteData?.quote_cocktails?.
        filter((item: any) => item?.provider == 'ho-provide');
    useEffect(() => {
        const fetchAndReset = async () => {
            if (!quote?.id) return;
            setQuoteLoading(true);
            try {
                const res = await GET(`${createQuote}/${quote.id}`, refreshToken);
                const data = res?.data?.data;
                setQuote(data);
            } catch (error) {
                console.log('Error fetching quote data:', error);
            } finally {
                setQuoteLoading(false);
            }
        };
        fetchAndReset();
    }, [quote?.id]);



    const salesTaxSum = (item: number) => {
        const num = (item ?? 0) + (item ? item * 0.10 : 0);
        return {
            string: fmt(centsToDollors(num)),
            num,
        }
    }
    const customerProvide = [
        {
            name: 'Ice & Coolers',
            qty: '40',
        },
        {
            name: 'Napkins',
            qty: '200',
        },
        {
            name: 'Vodka',
            qty: '6',
        },
    ]

    const payments = {
        deposit: quoteData?.deposit,
        platformFee: quoteData?.platform_fee,
        totalDeposit: quoteData?.total_deposit,
        secondPayment: quoteData && !quoteData?.is_full_payment ? Number(quote?.service_total) - Number(quoteData?.total_deposit) : 0,
        firstPaymentDueDate: quoteData?.first_payment_due_date,
        fullPayment: quoteData?.is_full_payment,
        secondPaymentDueDate: quoteData?.second_payment_due_date,
    }
    const DRINKER_LABELS: Record<Drinker, string> = {
        light: "Light",
        medium: "Standard",
        heavy: "Party",
    };
    const safeCapitalize = (v?: string | Drinker) =>
        typeof v === "string" && v.trim() ? toCaptilize(v) : "";
    const getDrinkerLabel = (v?: string | Drinker) =>
        typeof v === "string" && v in DRINKER_LABELS
            ? DRINKER_LABELS[v as Drinker]
            : safeCapitalize(v);
    const stageName = (item: any) => {
        const stage = selectedStages[item.id] ?? item.quote_stage;
        if (stage === 'inquiry') return 'lead';
        if (stage === 'manually-booked') return 'Manually Booked';
        return stage;
    }
    return (
        <div>
            {
                quoteLoading ?
                    <div><Loader color='bg-red-500' /></div>
                    :
                    <div className='flex flex-col gap-6 mb-10 mt-5'>
                        {!isTemplateOpen && <div className='flex justify-between'>
                            <div
                                onClick={() => setViewDetailModalOpen(false)}
                                className='bg-[#F7F8FA] p-2 rounded-full cursor-pointer flex justify-center items-center w-fit gap-2 text-gray-800 hover:bg-gray-200 transition'
                            >
                                <IoMdArrowBack />
                            </div>
                        </div>}
                        {isTemplateOpen &&
                            <div className='flex justify-end relative text-left'>
                                <SearchSelect
                                    value={{
                                        name: stageName(quote),
                                        value: (selectedStages[quote?.id] ?? quote?.quote_stage)?.toLowerCase(),
                                    }}
                                    onChange={(selected) => {
                                        setSelectedStages((prev) => ({
                                            ...prev,
                                            [quote?.id]: selected.value,
                                        }));

                                        updateQuoteStage?.(quote?.id, selected.value);
                                    }}
                                    options={[
                                        { name: 'Draft', value: 'draft' },
                                        { name: 'Proposed', value: 'proposed' },
                                        { name: 'Opened', value: 'opened' },
                                        { name: 'Archived', value: 'archived' }
                                    ]}
                                    data-testid='stage-select'
                                    placeholder='Select Stage'
                                />
                            </div>
                        }
                        {!isTemplate && <div className=' bg-white border border-gray-200 rounded-2xl p-6'>
                            {/* Header */}
                            <h2 className='text-xl font-semibold text-gray-900 mb-4'>Event Detail</h2>
                            {/* Client Details */}
                            <div className='mb-6'>
                                <div className='flex items-center space-x-2 mb-2'>
                                    <span className='text-orange-500 font-bold text-lg'>|</span>
                                    <h3 className='text-base font-semibold text-gray-900'>Client Details</h3>
                                </div>

                                <div className='ml-4 text-sm text-gray-700 space-y-1'>
                                    {(quoteData?.first_name || quoteData?.last_name) &&
                                        (<p>
                                            <span className='font-medium'>Name:</span> {quoteData?.first_name} {quoteData?.last_name}
                                        </p>)
                                    }
                                    {quoteData?.email && (
                                        <p>
                                            <span className='font-medium'>Email:</span> {quoteData?.email}
                                        </p>
                                    )}
                                    <p>
                                        <span className='font-medium'>Phone Number:</span> {quoteData?.phone}
                                    </p>
                                </div>
                            </div>

                            {/* Event Details */}
                            <div>
                                <div className='flex items-center space-x-2 mb-2'>
                                    <span className='text-orange-500 font-bold text-lg'>|</span>
                                    <h3 className='text-base font-semibold text-gray-900'>Event Details</h3>
                                </div>

                                <div className='ml-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-2 text-sm text-gray-700'>
                                    {quoteData?.address1 && <p>
                                        <span className='font-medium'>Address:</span> {quoteData?.address1} {quoteData?.city}
                                    </p>}
                                    {quoteData?.guests && <p>
                                        <span className='font-medium'>No of guests:</span> {quoteData?.guests}
                                    </p>}
                                    {quoteData?.start_time && <p>
                                        <span className='font-medium'>Event Date:</span> {formattedDate(splitEventDateAndTime(quoteData.start_time)?.eventDate)}
                                    </p>}
                                    {quoteData?.start_time && quoteData?.end_time && <p>
                                        <span className='font-medium'>Time:</span> {splitEventDateAndTime(quoteData.start_time)?.formattedTime} – {splitEventDateAndTime(quoteData.end_time)?.formattedTime}
                                    </p>}
                                    {/* {quoteData?.drinker_type && <p>
                                        <span className='font-medium'>Type of Drinker:</span>{' '}
                                        <span className='text-gray-500'>{getDrinkerLabel(quoteData?.drinker_type)}</span>
                                    </p>} */}
                                    {quoteData?.event_type && <p>
                                        <span className='font-medium'>Event Type:</span> {EVENT_TYPE_MAP[quoteData.event_type]?.name ?? toCaptilize(quoteData.event_type)}
                                    </p>}
                                    {quoteData?.type_of_service && <p>
                                        <span className='font-medium'>Type of Service:</span>{' '}
                                        {TypeOfServiceOptions.find((o) => o.value === quoteData.type_of_service)?.name ?? toCaptilize(quoteData.type_of_service)}
                                    </p>}
                                    {quoteData?.additional_info && <p className='sm:col-span-2 lg:col-span-3'>
                                        <span className='font-medium'>Additional Information:</span>{' '}
                                        {quoteData.additional_info}
                                    </p>}
                                </div>
                            </div>
                        </div>}
                        <div className=' bg-white border border-gray-200 rounded-2xl p-6'>
                            {/* Header */}
                            <h2 className='text-xl font-semibold text-gray-900 mb-4'>Provisional Invoice</h2>
                            {(bartenderPackageCompany && bartenderPackageCustomer) && <div>
                                {/* Sub-header */}
                                <h3 className='text-base font-semibold text-gray-800 mb-4'>
                                    Bartending Package Details
                                </h3>
                                {bartenderPackageCompany.length > 0 && <div>
                                    {/* Table */}
                                    <div className='overflow-x-auto'>
                                        <table className='w-full text-sm text-left text-gray-700 border-t border-gray-200'>
                                            <thead className='text-gray-500 text-xs uppercase border-b border-gray-200'>
                                                <tr>
                                                    <th className='px-4 py-3 font-bold'>Item Detail</th>
                                                    <th className='px-4 py-3 font-bold text-center'>Quantity</th>
                                                    <th className='px-4 py-3 font-bold text-center'>Rate</th>
                                                    <th className='px-4 py-3 font-bold text-right'>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bartenderPackageCompany?.map((item: any, index: any) => {
                                                    return (
                                                        <tr className='border-b border-gray-100' key={index}>
                                                            <td className='px-4 py-3 font-medium'>{item?.name ?? '-'}</td>
                                                            <td className='px-4 py-3 text-center'>{item?.qty ?? '-'}</td>
                                                            <td className='px-4 py-3 text-center'>{fmt(centsToDollors(item?.price_per_item)) ?? '-'}</td>
                                                            <td className='px-4 py-3 text-right'>{fmt(centsToDollors(item?.line_total)) ?? '-'}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    {/* Summary */}
                                    <div className="mt-6 grid grid-cols-2 gap-4  w-full">
                                        <div className="w-full">{/* LEFT COLUMN */}</div>
                                        <div className="text-sm w-full">
                                            {/* RIGHT COLUMN */}
                                            <Row label="Subtotal" value={fmt(centsToDollors(quote?.bartender_package_total ?? 0))} />
                                            {Number(quote?.discount_total) > 0 && <Row label="Discount" value={fmt(centsToDollors(quote?.discount_total ?? 0))} />}
                                            {/* <Row label="HOT Platform Fee" value={fmt(centsToDollors(quote?.platform_fee ?? 0))} /> */}
                                            <div className="mt-3 flex items-center justify-between border-t pt-3 border-gray-200">
                                                <span className="text-sm font-medium text-gray-700">Total</span>
                                                <span className="text-lg font-semibold text-gray-900">
                                                    {fmt(centsToDollors(quote?.service_total ?? 0))}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>}
                                {/* Customer Provides */}
                                {bartenderPackageCustomer.length > 0 &&
                                    <div className="mt-6 rounded-2xl bg-gray-50 p-4">
                                        <p className="text-sm text-gray-800 mb-3 text-[10px] font-[600]">
                                            Client Provides
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {bartenderPackageCustomer.map((item: any, i: any) => (
                                                <span
                                                    key={i}
                                                    className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm"
                                                >
                                                    <span className="text-[10px]">{item?.name}:</span>
                                                    <span className="inline-flex h-6 items-center px-2 text-[10px] text-[#8D8D8D] ">
                                                        {item?.qty}
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>}
                            </div>}
                            {/* Estimated Alcohol Details */}
                            {!isTemplate && (cocktailCustomer || cocktailHoProvide || cocktailCompany) && <div>
                                {/* Header */}
                                <h2 className='text-xl font-semibold text-gray-700 mb-4 mt-8'>Estimated Alcohol Details</h2>
                                {/* Table */}
                                <div className='overflow-x-auto'>
                                    <table className='w-full text-sm text-left text-gray-700 border-t border-gray-200'>
                                        <thead className='text-gray-500 text-xs uppercase border-b border-gray-200'>
                                            <tr>
                                                <th className='px-4 py-3 font-bold'>Item Detail</th>
                                                <th className='px-4 py-3 font-bold'>Description</th>
                                                <th className='px-4 py-3 font-bold text-center'>Quantity</th>
                                                <th className='px-4 py-3 font-bold text-center'>Rate</th>
                                                <th className='px-4 py-3 font-bold text-right'>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cocktailCompany.length > 0 && cocktailCompany.map((item: any) => (
                                                <tr key={item?.id} className='border-b border-gray-100'>
                                                    <td className='px-4 py-3 font-medium'>{
                                                        CocktailProviders.find(
                                                            (provider) => provider.value === item?.provider
                                                        )?.name ?? null
                                                    }</td>
                                                    <td className='px-4 py-3'>{item?.brand_name ?? '-'}</td>
                                                    <td className='px-4 py-3 text-center'>{item?.quantity ?? '-'}</td>
                                                    <td className='px-4 py-3 text-center'>{fmt(centsToDollors(item?.price_per_item)) ?? '-'}</td>
                                                    <td className='px-4 py-3 text-right font-medium'>{fmt(centsToDollors(Number(item?.price_per_item) * Number(item?.quantity)))}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Summary */}
                                <div className="mt-6 grid grid-cols-2 gap-4  w-full ">
                                    <div className="w-full">{/* LEFT COLUMN */}</div>
                                    <div className="text-sm w-full">
                                        {/* RIGHT COLUMN */}
                                        <Row label="Subtotal" value={fmt(centsToDollors(quote?.estimated_alcohol_sub_total ?? 0))} />
                                        <Row label="Sales Tax" value={fmt(centsToDollors(quote?.estimated_alcohol_sub_total ? quote?.estimated_alcohol_sub_total * 0.10 : 0))} />
                                        {/* <Row label="HOT Platform Fee" value={fmt(centsToDollors(quote?.alcohol_platform_fee_total ?? 0))} /> */}
                                        <div className="mt-3 flex items-center justify-between border-t pt-3 border-gray-200">
                                            <span className="text-sm font-medium text-gray-700">Total</span>
                                            <span className="text-lg font-semibold text-gray-900">
                                                {salesTaxSum(quote?.estimated_alcohol_sub_total ?? 0).string}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {/* <div className="flex items-center gap-2 mt-4">
                                                                                <InfoIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                                                <p className="text-gray-400 text-xs">
                                                                                    This may not be the final alcohol charge. If preferred brands are selected we will charge based off of the bottles used after the event. If preferred brands are not selected then each bottle is final sale.
                                                                                </p>
                                                                            </div> */}
                                {/* Customer Provides */}
                                {cocktailCustomer.length > 0 && <div className="mt-6 rounded-2xl bg-gray-50 p-4">
                                    <p className="text-sm text-gray-800 mb-3 text-[10px] font-[600]">
                                        Client Provides
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {cocktailCustomer.map((item: any) => (
                                            <span
                                                key={item?.id}
                                                className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm"
                                            >
                                                <span className="text-[10px]">{item?.brand_name ?? '-'}:</span>
                                                <span className="inline-flex h-6 items-center px-2 text-[10px] text-[#8D8D8D] ">
                                                    {item?.quantity ?? '-'}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>}
                                {/* Selling Cocktails */}
                                {cocktailHoProvide.length > 0 && <div className="mt-6 rounded-2xl bg-gray-50 p-4">
                                    <p className="text-sm text-gray-800 mb-3 text-[10px] font-[600]">
                                        Selling Cocktails
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {cocktailHoProvide.map((item: any) => (
                                            <span
                                                key={item?.id}
                                                className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm"
                                            >
                                                <span className="text-[10px]">{item?.brand_name ?? '-'}:</span>
                                                <span className="inline-flex h-6 items-center px-2 text-[10px] text-[#8D8D8D] ">
                                                    {item?.quantity ?? '-'}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>}
                            </div>}
                            {/* Addtional Information*/}
                            {/* Header */}
                            <h2 className='text-xl font-semibold text-gray-900 mb-2 mt-4'>Addtional Information</h2>
                            {/* Sub-header */}
                            <h3 className=' text-gray-400 text-xs'>
                                {quote?.notes}
                            </h3>
                        </div>
                        {!isTemplate && <div className=' bg-white border border-gray-200 rounded-2xl p-6'>
                            <div className="w-full bg-white">
                                <h2 className='text-xl font-semibold text-gray-900 mb-2'> Payment Summary</h2>
                                <div className="overflow-hidden">
                                    <div className="">
                                        {payments.fullPayment && <PaymentPreviewRow
                                            label="Full Payment"
                                            date={formattedDate(payments.firstPaymentDueDate)}
                                            amount={centsToDollors(payments?.totalDeposit ?? 0)}
                                        />
                                        }
                                        {!payments.fullPayment && <PaymentPreviewRow
                                            label="First Payment"
                                            date={formattedDate(payments.firstPaymentDueDate)}
                                            amount={centsToDollors(payments?.totalDeposit ?? 0)}
                                        />
                                        }
                                        {!payments.fullPayment || !payments?.secondPayment && <PaymentPreviewRow
                                            label="Second Payment"
                                            date={formattedDate(payments.secondPaymentDueDate)}
                                            amount={centsToDollors(payments?.secondPayment ?? 0)}
                                        />
                                        }
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <div className="ml-auto w-full max-w-xs text-sm">
                                        <Row
                                            label="Bartending Package Total"
                                            value={fmt(centsToDollors(quote?.service_total ?? 0))}
                                        />
                                        <Row
                                            label="Estimated Alcohol"
                                            value={salesTaxSum(quote?.estimated_alcohol_sub_total ?? 0).string}
                                        />
                                        {/* <Row
                                            label="Platform Fee"
                                            value={fmt(centsToDollors(Number(payments?.platformFee ?? 0)))}
                                        /> */}
                                        <div className="mt-3 pt-3 flex items-center justify-between border-gray-200 border-t">
                                            <span className="text-[13px] text-gray-600">
                                                Estimated Total Cost
                                            </span>
                                            <span className="text-lg font-semibold text-gray-900">
                                                {fmt(centsToDollors(Number(quote?.service_total ?? 0) + Number(salesTaxSum(quote?.estimated_alcohol_sub_total ?? 0).num)))}
                                                {/* {fmt(Number(quoteData?.estimated_cost_total))} */}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>}
                    </div>
            }
        </div>
    );
};

export default PreviewQuoteTemplate;

function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center justify-between text-[12px] font-[400]">
            <span className="text-gray-600">{label}</span>
            <span className="text-gray-800">{value}</span>
        </div>
    );
}

function InfoIcon({ className = "h-4 w-4" }: { className?: string }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            className={className}
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
            <path
                d="M12 10.5v5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
            <circle cx="12" cy="7.75" r=".9" fill="currentColor" />
        </svg>
    );
}

function PaymentPreviewRow({ label, date, amount }: any) {
    return (
        <div
            className={`grid grid-cols-3 font-[500] text-xs text-[#202224] border-gray-200 border-t "border-b"`}>
            <div className="px-4 py-4 text-left text-[10px]">{label}</div>
            <div className="px-4 py-4 text-gray-600 text-center text-[10px]">
                {date}
            </div>
            <div className="px-4 py-4 text-gray-900 text-right text-[10px]">
                {fmt(amount)}
            </div>
        </div>
    )
}
